/**
 * parseNumericInput — Zahleneingaben aus Textfeldern robust lesen.
 *
 * ══ Warum das existiert ═════════════════════════════════════════════════════
 * Auf einer deutschen Tastatur ist das Komma das Dezimaltrennzeichen. `parseFloat`
 * kennt nur den Punkt und bricht beim Komma einfach ab:
 *
 *     parseFloat('12,5')  →  12      (nicht 12,5)
 *     parseInt('1.200')   →  1       (nicht 1200)
 *
 * Beides schlägt STILL fehl — kein Fehler, keine Warnung, nur ein falscher Wert
 * in der Bilanz. Genau die Art Fehler, die eine Tracking-App unbrauchbar macht,
 * weil der Nutzer die Korrektur für erledigt hält.
 *
 * Diese Funktion ist die eine Stelle, an der Nutzereingaben zu Zahlen werden.
 * Vorher hatte praktisch jedes Eingabefeld seine eigene kleine Variante — und
 * ausgerechnet die Nährwert-Felder hatten gar keine.
 *
 * ══ Regeln für Trennzeichen ═════════════════════════════════════════════════
 * Eindeutige Fälle werden korrekt aufgelöst, mehrdeutige konservativ:
 *   "12,5"       → 12.5    (Komma = Dezimaltrennzeichen)
 *   "12.5"       → 12.5    (Punkt = Dezimaltrennzeichen)
 *   "1.200"      → 1.2     (mehrdeutig — bleibt beim Punkt als Dezimaltrenner)
 *   "1.200.000"  → 1200000 (mehrere Punkte können nur Tausender sein)
 *   "1.200,50"   → 1200.5  (beide vorhanden → das LETZTE trennt die Dezimalen)
 *   "450 kcal"   → 450     (Einheiten werden ignoriert)
 *   ""           → null    (leer ist keine Null, siehe unten)
 *
 * ══ Warum null statt 0 ══════════════════════════════════════════════════════
 * Ein leeres Feld bedeutet nicht „null Kalorien" — meistens hat der Nutzer es
 * geleert, um neu zu tippen. Wer hier 0 zurückgibt, schreibt eine 0 in die
 * Bilanz, sobald jemand mit leerem Feld auf Bestätigen tippt. Der Aufrufer
 * entscheidet bewusst, was ein leeres Feld heißen soll.
 */

/**
 * Eine Zahl aus einer Texteingabe lesen.
 *
 * @returns Die Zahl, oder `null` wenn die Eingabe leer oder unlesbar ist.
 */
export function parseNumericInput(raw: string | null | undefined): number | null {
  if (typeof raw !== 'string') return null;

  // Alles außer Ziffern, Trennzeichen und Vorzeichen entfernen (Einheiten,
  // Leerzeichen, geschützte Leerzeichen aus Copy-Paste).
  const cleaned = raw.replace(/[^\d.,-]/g, '');
  if (!cleaned || !/\d/.test(cleaned)) return null;

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');

  let normalized: string;
  if (lastComma >= 0 && lastDot >= 0) {
    // Beide vorhanden: das weiter hinten stehende trennt die Dezimalstellen,
    // das andere gruppiert Tausender.
    const decimalSep = lastComma > lastDot ? ',' : '.';
    const groupSep = decimalSep === ',' ? '.' : ',';
    normalized = cleaned.split(groupSep).join('').replace(decimalSep, '.');
  } else if (lastComma >= 0) {
    // Nur Kommas. Eines trennt Dezimalstellen, mehrere können nur gruppieren.
    normalized =
      cleaned.indexOf(',') === lastComma
        ? cleaned.replace(',', '.')
        : cleaned.split(',').join('');
  } else if (lastDot >= 0 && cleaned.indexOf('.') !== lastDot) {
    // Mehrere Punkte → Tausendergruppierung.
    normalized = cleaned.split('.').join('');
  } else {
    normalized = cleaned;
  }

  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

/**
 * Wie `parseNumericInput`, aber mit Rückfallwert für leere/unlesbare Eingaben.
 * Für Felder, bei denen ein leeres Feld den bisherigen Wert behalten soll.
 */
export function parseNumericInputOr(
  raw: string | null | undefined,
  fallback: number,
): number {
  const n = parseNumericInput(raw);
  return n === null ? fallback : n;
}
